import { useEffect, useMemo } from 'react';
import { useLinksContext } from '@/context/LinksContext';
import { useToolSettings } from '@/context/ToolSettingsContext';
import { useSelectedStudent } from '@/context/SelectedStudentContext';
import { useOverviewContext } from '@/context/OverviewContext';
import {
  getK12SchoolPortalResolution,
  type K12SchoolPortalUiSnapshot,
  type TasklistDisplayRuntime,
} from '@/utils/tasklistPageDisplay';
import { tasklistDisplayGradeId } from '@/utils/toolSettingsUtils';

export interface UseTasklistDisplayRuntimeResult extends K12SchoolPortalUiSnapshot {
  /** Both school-link and tool-settings fetches have settled (or were skipped). */
  isReady: boolean;
}

/**
 * Shared OLS / Launch Pad runtime for CMS `displayConditions` on secondary pages.
 * `olsAvailable` from API `courseAssigned`; `launchpadAvailable` from tool 200.
 */
export function useTasklistDisplayRuntime(): UseTasklistDisplayRuntimeResult {
  const { schoolLink, quicklinksLoaded } = useLinksContext();
  const { toolSettings, toolSettingsLoaded } = useToolSettings();
  const { selectedStudent } = useSelectedStudent();
  const { userRoleVal: accountRoleVal } = useOverviewContext();

  const gradeId = tasklistDisplayGradeId(selectedStudent?.gradeId, accountRoleVal);

  const tool200Active = useMemo(() => {
    if (200 in toolSettings) return toolSettings[200];
    return undefined;
  }, [toolSettings]);

  const snapshot = useMemo(
    () => getK12SchoolPortalResolution(schoolLink, gradeId, tool200Active),
    [schoolLink, gradeId, tool200Active]
  );

  const isReady = quicklinksLoaded && toolSettingsLoaded;

  useEffect(() => {
    console.log('[OLS/LP display runtime]', {
      olsAvailable: snapshot.runtime.olsAvailable,
      launchpadAvailable: snapshot.runtime.launchpadAvailable,
      showK12SchoolCta: snapshot.showK12SchoolCta,
      k12SchoolHref: snapshot.k12SchoolHref,
      showCanvasInlineLinks: snapshot.showCanvasInlineLinks,
      schoolLink,
      gradeId,
      tool200Active,
      isReady,
    });
  }, [
    snapshot.runtime.olsAvailable,
    snapshot.runtime.launchpadAvailable,
    snapshot.showK12SchoolCta,
    snapshot.k12SchoolHref,
    snapshot.showCanvasInlineLinks,
    schoolLink,
    gradeId,
    tool200Active,
    isReady,
  ]);

  return { ...snapshot, isReady };
}

export type { TasklistDisplayRuntime };
